"use client";

import React from "react";
import { Text, SectionCard, CardTitle, Section } from "@/src/components";
import { ChartProvider } from "@/src/components/charts/chart-provider";
import { DonutChart } from "@/src/components/charts/donut-chart";

const paymentMethods = {
  items: [
    { name: "Credit Card", value: 4200 },
    { name: "Bank Transfer", value: 3200 },
    { name: "PayPal", value: 2100 },
    { name: "Other", value: 1500 },
  ],
};

const completionData = {
  items: [
    { name: "Completed", value: 75 },
    { name: "Pending", value: 25 },
  ],
};

const distributionData = {
  items: [
    { name: "Product A", value: 1200 },
    { name: "Product B", value: 980 },
    { name: "Product C", value: 650 },
    { name: "Product D", value: 470 },
    { name: "Product E", value: 300 },
  ],
};

export default function DocsDonutChartPage() {
  return (
    <ChartProvider>
      <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
        {/* Header */}
        <Section>
          <div className="space-y-2">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Donut Chart
            </Text>
            <Text className="text-base text-muted-foreground max-w-3xl">
              Pie chart variant with a hollow center. Use the center space for
              additional context, metrics, or labels.
            </Text>
          </div>
        </Section>

        {/* Basic Example */}
        <Section title="Basic Example">
          <SectionCard
            header={<CardTitle>Payment Method Distribution</CardTitle>}
            contentClassName="p-theme-5"
          >
            <DonutChart
              data={paymentMethods}
              height={350}
              showLegend={true}
              thickness={40}
            />
          </SectionCard>
        </Section>

        {/* With Center Text */}
        <Section title="With Center Text">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-theme-4">
            <SectionCard
              header={<CardTitle>Completion Percentage</CardTitle>}
              contentClassName="p-theme-5"
            >
              <DonutChart
                data={completionData}
                height={300}
                centerText="75%"
                centerSubText="Complete"
                thickness={50}
              />
            </SectionCard>

            <SectionCard
              header={<CardTitle>Product Distribution</CardTitle>}
              contentClassName="p-theme-5"
            >
              <DonutChart
                data={distributionData}
                height={300}
                centerText="5,600"
                centerSubText="Total Units"
                thickness={45}
              />
            </SectionCard>
          </div>
        </Section>

        {/* Thick Donut */}
        <Section title="Thickness Variants">
          <SectionCard
            header={<CardTitle>Custom Thickness</CardTitle>}
            contentClassName="p-theme-5"
          >
            <DonutChart
              data={paymentMethods}
              height={350}
              centerText="100%"
              thickness={70}
            />
          </SectionCard>
        </Section>

        {/* Props Table */}
        <Section title="Props">
          <SectionCard
            header={<CardTitle>DonutChartProps</CardTitle>}
            contentClassName="p-theme-5 overflow-x-auto"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-semibold">Property</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Default</th>
                  <th className="text-left py-2 px-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">data</td>
                  <td className="py-2 px-2">DonutChartData</td>
                  <td className="py-2 px-2">Required</td>
                  <td className="py-2 px-2">Array of items with name and value</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">title</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart title</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">subtitle</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart subtitle</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">height</td>
                  <td className="py-2 px-2">string | number</td>
                  <td className="py-2 px-2">300</td>
                  <td className="py-2 px-2">Chart height in px or CSS value</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">thickness</td>
                  <td className="py-2 px-2">number</td>
                  <td className="py-2 px-2">60</td>
                  <td className="py-2 px-2">Ring thickness (0-100)</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">centerText</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Main text in center (large font)</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">centerSubText</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Subtitle text in center</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showLegend</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Show legend</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showLabels</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show labels on slices</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">className</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Additional CSS classes</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">loading</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show loading state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">empty</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show empty state</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">onSliceClick</td>
                  <td className="py-2 px-2">(params) =&gt; void</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Click event handler for slices</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </Section>

        {/* Data Structure */}
        <Section title="Data Structure">
          <SectionCard
            header={<CardTitle>DonutChartData Interface</CardTitle>}
            contentClassName="p-theme-5"
          >
            <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
              <code>{`interface DonutChartData {
  items: {
    name: string;           // Slice label / legend name
    value: number;          // Numeric value for slice size
    color?: string;         // Optional custom color (hex/CSS)
  }[];
}

// Example:
const data = {
  items: [
    {
      name: "Credit Card",
      value: 4200
    },
    {
      name: "Bank Transfer",
      value: 3200
    },
    {
      name: "PayPal",
      value: 2100
    }
  ]
};

// With center text:
<DonutChart
  data={data}
  centerText="9,500"
  centerSubText="Total"
  thickness={50}
/>`}</code>
            </pre>
          </SectionCard>
        </Section>
      </div>
    </ChartProvider>
  );
}

export { DocsDonutChartPage as DonutChartPage };
